import { useEffect, useState } from 'react';
import { formatDateTime } from '@nu/shared';
import { api } from '../api/client.js';
import DigitalRiskAssessment from './DigitalRiskAssessment.jsx';

const KIND = { ra_front: 'Risk assessment page 1', ra_back: 'Risk assessment page 2', before: 'Before works', after: 'After works', abort: 'Abort', circle_back: 'Circle back', incident: 'Incident', meter: 'Meter fitted' };
// The photos split by what the engineer took them as, in the order of the
// job's steps. `short` is the label under each thumbnail.
const GROUPS = [
  { title: 'Risk assessment', kinds: { ra_front: 'Page 1', ra_back: 'Page 2' } },
  { title: 'Works', kinds: { before: 'Before', after: 'After' } },
  { title: 'Meters fitted', kinds: { meter: 'Meter' } },
  { title: 'Incidents', kinds: { incident: 'Incident' } },
  { title: 'Abort', kinds: { abort: 'Abort' } },
  { title: 'Circle back', kinds: { circle_back: 'Circle back' } },
];
// One photo of these counts; a newer one replaces it (a retake).
const SINGLE = ['ra_front', 'ra_back', 'before', 'after'];
const time = (iso) => formatDateTime(iso).slice(-5);

// A job's photos as thumbnails, grouped by step (risk assessment, works,
// meters...), each labelled with what it was taken as and when; tap one to
// see it large. A retaken photo shows as Replaced next to the one that
// counts. `base` is the photos endpoint (/bookings/:id/photos for the office,
// /my/jobs/:id/photos in the app). Shows nothing when there are none or
// photos aren't set up. incidentId shows only that incident's photos.
// digitiseId (the office's booking pop-up): the booking, for Digitise risk
// assessment under its risk assessment photos.
export default function JobPhotos({ base, title = 'Photos', incidentId = null, digitiseId = null, digitiseRef = '' }) {
  const [photos, setPhotos] = useState([]);
  const [big, setBig] = useState(null);

  useEffect(() => {
    let live = true;
    const urls = [];
    (async () => {
      const list = await api(base).catch(() => []);
      for (const p of list.filter((x) => !incidentId || x.incidentId === incidentId)) {
        const blob = await api(`${base}/${p.id}`, { as: 'blob' }).catch(() => null);
        if (!live) return;
        if (!blob) continue;
        const url = URL.createObjectURL(blob);
        urls.push(url);
        setPhotos((x) => [...x, { ...p, url }]);
      }
    })();
    return () => {
      live = false;
      urls.forEach((u) => URL.revokeObjectURL(u));
      setPhotos([]);
    };
  }, [base, incidentId]);

  if (!photos.length) return null;
  const sorted = [...photos].sort((a, b) => String(a.takenAt).localeCompare(String(b.takenAt)));
  const counts = {};
  for (const p of sorted) if (SINGLE.includes(p.kind)) counts[p.kind] = p.id; // the newest wins
  const known = new Set(GROUPS.flatMap((g) => Object.keys(g.kinds)));
  const groups = [
    ...GROUPS.map((g) => ({ ...g, photos: sorted.filter((p) => g.kinds[p.kind]) })),
    { title: 'Other', kinds: {}, photos: sorted.filter((p) => !known.has(p.kind)) },
  ].filter((g) => g.photos.length);
  // Ordered by step, then by kind within it (page 1 before page 2).
  for (const g of groups) {
    const order = Object.keys(g.kinds);
    g.photos.sort((a, b) => order.indexOf(a.kind) - order.indexOf(b.kind) || String(a.takenAt).localeCompare(String(b.takenAt)));
  }
  const replaced = (p) => SINGLE.includes(p.kind) && counts[p.kind] !== p.id;

  return (
    <div className="job-photos">
      <h3>{title} ({photos.length})</h3>
      {groups.map((g) => {
        const list = (
          <ul className="photo-picks photo-labelled">
            {g.photos.map((p) => (
              <li key={p.id} className={replaced(p) ? 'replaced' : ''}>
                <button type="button" className="photo-open" onClick={() => setBig(p)} aria-label={`Open ${KIND[p.kind] || 'job'} photo, ${formatDateTime(p.takenAt)}${replaced(p) ? ', replaced' : ''}`}>
                  <img src={p.url} alt="" />
                </button>
                <span className="photo-caption">
                  <strong>{g.kinds[p.kind] || KIND[p.kind] || 'Photo'}</strong> {time(p.takenAt)}
                  {replaced(p) && <span className="photo-replaced">Replaced</span>}
                </span>
              </li>
            ))}
          </ul>
        );
        return (
          <section key={g.title} className="photo-group">
            {groups.length > 1 && <h4>{g.title}</h4>}
            {digitiseId && g.title === 'Risk assessment'
              ? <DigitalRiskAssessment bookingId={digitiseId} reference={digitiseRef}>{list}</DigitalRiskAssessment>
              : list}
          </section>
        );
      })}
      {big && (
        <div className="popup-backdrop" onMouseDown={() => setBig(null)}>
          <figure className="photo-big">
            <img src={big.url} alt={`${KIND[big.kind] || 'Job'} photo`} />
            <figcaption>{KIND[big.kind] || 'Job'} photo{replaced(big) ? ' (replaced by a retake)' : ''} · {formatDateTime(big.takenAt)} · {big.takenByEmail}</figcaption>
            <button type="button" className="btn popup-btn" onClick={() => setBig(null)}>Close</button>
          </figure>
        </div>
      )}
    </div>
  );
}
